import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { DownloadOutlined, ExportOutlined } from "@ant-design/icons";
import { Avatar, Button, Divider, Modal, Spin, theme } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect } from "react";
import { trackAnalyticsEvent } from "../../../../others/tracking.js";
import { useStores } from "../../../../store/stores.js";
import { COLOR_PRIMARY_SOFT_BG } from "../../../../style/antdTheme.js";
import { formatLocaleDate } from "../../../../utils/formatLocaleDate.js";
import { __ } from "../../../../utils/i18n.js";
import { BillingAddressRestrictions } from "../billingAddressRestrictions.js";
import { EmptyState } from "../emptyState.js";
const ChangeInvoiceAddressModal = observer(({ subscription })=>{
    const { accountV2Store } = useStores();
    const { session } = accountV2Store;
    const { token } = theme.useToken();
    const close = useCallback(()=>accountV2Store.closeSubscriptionModal(), [
        accountV2Store
    ]);
    // Fetch invoices lazily when the modal opens
    useEffect(()=>{
        if (session && session.invoices.length === 0 && !session.isBusyInvoices) {
            session.fetchInvoices();
        }
    }, []);
    // Latest invoice for this subscription — match by product plan ID
    const latestInvoice = session?.invoices.find((invoice)=>invoice.data.productPlan.id === subscription.data.productPlan.id);
    let invoiceContent;
    if (session?.isBusyInvoices) {
        invoiceContent = /*#__PURE__*/ _jsx("div", {
            className: "rc-account-v2-change-invoice-address__frame rc-account-v2-change-invoice-address__frame--loading",
            children: /*#__PURE__*/ _jsx(Spin, {})
        });
    } else if (latestInvoice) {
        const { data } = latestInvoice;
        const logoUrl = data.product.logoSquare?.downloadUrl;
        const dateLabel = formatLocaleDate(data.createdAt);
        invoiceContent = /*#__PURE__*/ _jsxs("div", {
            className: "rc-account-v2-change-invoice-address__frame rc-account-v2-change-invoice-address__product-row",
            children: [
                logoUrl ? /*#__PURE__*/ _jsx(Avatar, {
                    shape: "square",
                    src: logoUrl,
                    size: 36,
                    style: {
                        borderRadius: 6,
                        border: `1px solid ${token.colorBorder}`,
                        flexShrink: 0
                    }
                }) : /*#__PURE__*/ _jsx(Avatar, {
                    shape: "square",
                    size: 36,
                    style: {
                        borderRadius: 6,
                        background: COLOR_PRIMARY_SOFT_BG,
                        color: token.colorPrimary,
                        fontWeight: 600,
                        fontSize: 15,
                        flexShrink: 0
                    },
                    children: data.product.name.charAt(0).toUpperCase()
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-change-invoice-address__product-info",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-change-invoice-address__product-name",
                            style: {
                                color: token.colorTextHeading
                            },
                            children: data.productPlan.name
                        }),
                        data.productPlan.description && /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-change-invoice-address__product-plan",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: data.productPlan.description
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-change-invoice-address__invoice-meta",
                            style: {
                                color: token.colorTextSecondary
                            },
                            children: __("Invoice date: %s", dateLabel)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-change-invoice-address__product-actions",
                    children: [
                        data.invoiceUrl && /*#__PURE__*/ _jsx(Button, {
                            type: "primary",
                            icon: /*#__PURE__*/ _jsx(ExportOutlined, {}),
                            onClick: ()=>{
                                trackAnalyticsEvent("invoice_open", {
                                    ga: {
                                        event_category: "account",
                                        event_label: "Invoice open",
                                        value: data.productPlan?.name
                                    },
                                    matomo: {
                                        category: "account",
                                        name: "Invoice open"
                                    }
                                });
                                window.open(data.invoiceUrl, "_blank");
                            },
                            children: __("Open invoice")
                        }),
                        data.invoiceUrlPdf && /*#__PURE__*/ _jsx(Button, {
                            icon: /*#__PURE__*/ _jsx(DownloadOutlined, {}),
                            onClick: ()=>{
                                trackAnalyticsEvent("invoice_download", {
                                    ga: {
                                        event_category: "account",
                                        event_label: "Invoice download",
                                        value: data.productPlan?.name
                                    },
                                    matomo: {
                                        category: "account",
                                        name: "Invoice download"
                                    }
                                });
                                window.open(data.invoiceUrlPdf, "_blank");
                            },
                            children: __("Download")
                        })
                    ]
                })
            ]
        });
    } else {
        invoiceContent = /*#__PURE__*/ _jsx("div", {
            className: "rc-account-v2-change-invoice-address__frame",
            children: /*#__PURE__*/ _jsx(EmptyState, {
                heading: __("No invoice yet."),
                subline: __("No invoice has been issued for this subscription yet.")
            })
        });
    }
    return /*#__PURE__*/ _jsxs(Modal, {
        open: true,
        title: /*#__PURE__*/ _jsxs("div", {
            className: "rc-account-v2-change-invoice-address__modal-title",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "rc-account-v2-change-invoice-address__modal-heading",
                    style: {
                        color: token.colorTextHeading
                    },
                    children: __("Change invoice address")
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "rc-account-v2-change-invoice-address__modal-subtitle",
                    style: {
                        color: token.colorText
                    },
                    children: __("To update your billing address, open the latest invoice for this subscription below and click 'Add address & VAT number' or 'Edit address / add VAT number' (personal) or 'Edit business address' (company/organization) on the invoice page.")
                })
            ]
        }),
        onCancel: close,
        footer: null,
        width: 640,
        destroyOnClose: true,
        children: [
            /*#__PURE__*/ _jsx(Divider, {
                style: {
                    marginTop: 0
                }
            }),
            invoiceContent,
            /*#__PURE__*/ _jsx("p", {
                className: "rc-account-v2-info-card-para rc-account-v2-change-invoice-address__restrictions-note",
                style: {
                    color: token.colorTextSecondary
                },
                children: __("The updated address applies to this invoice and all future ones. To update your address for upcoming invoices, wait for the next invoice to be issued and open it here.")
            }),
            /*#__PURE__*/ _jsx(BillingAddressRestrictions, {})
        ]
    });
});
export { ChangeInvoiceAddressModal };
